import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SpecialityAccessFields, SPECIALITIES } from "@/components/SpecialityAccessFields";
import { UserPlus, Loader2, Copy, Check, MailWarning } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [["1", "1 month"], ["3", "3 months"], ["6", "6 months"], ["12", "12 months"], ["none", "No expiry"]];
const EMPTY = { name: "", email: "", org_name: "", plan: "support", duration: "3", speciality: "", full_access: false };

function InviteResult({ r, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(r.invite_link);
    setCopied(true); toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };
  const spec = SPECIALITIES.find(([v]) => v === r.speciality)?.[1];
  return (
    <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-2" data-testid="invite-result">
      <p className="font-medium text-emerald-900">
        Account created for {r.email}{spec ? ` · ${spec}` : ""}{r.full_access ? ` · ${r.unlocked} packs unlocked` : ""}
      </p>
      {!r.email_sent && (
        <p className="flex items-center gap-1.5 text-amber-800" data-testid="invite-email-failed">
          <MailWarning className="w-4 h-4" /> The invite email couldn't be sent — share the link below instead.
        </p>
      )}
      <p className="text-muted-foreground">Their one-time set-password link (valid 7 days):</p>
      <div className="flex gap-2">
        <Input readOnly value={r.invite_link} className="font-mono text-xs bg-white" data-testid="invite-link" onFocus={(e) => e.target.select()} />
        <Button type="button" variant="outline" onClick={copy} data-testid="invite-copy-link">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={onDismiss} data-testid="invite-result-dismiss">Invite another</Button>
    </div>
  );
}

export default function AdminInviteAccess({ onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/invite-with-access", {
        ...form, speciality: form.speciality || null, duration_months: form.duration === "none" ? null : Number(form.duration),
      });
      toast.success(data.email_sent ? `Invite sent to ${form.email}` : "Account created — copy the link below");
      setResult(data); setForm(EMPTY); onCreated?.(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send invite"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-invite-access">
      <p className="font-display font-semibold flex items-center gap-2 mb-1"><UserPlus className="w-4 h-4 text-accent" /> Invite someone with free access</p>
      <p className="text-xs text-muted-foreground mb-4">Creates their account with the plan already active and emails a 7-day "set your password" link. Pick a speciality to set their dashboard, inspection framework and updates feed — and optionally unlock every policy pack for it. No code, no card.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-6 gap-2" data-testid="invite-access-form">
        <Input value={form.name} onChange={set("name")} placeholder="Full name" required className="sm:col-span-2" data-testid="invite-name" />
        <Input type="email" value={form.email} onChange={set("email")} placeholder="Email" required className="sm:col-span-2" data-testid="invite-email" />
        <Input value={form.org_name} onChange={set("org_name")} placeholder="Organisation (optional)" className="sm:col-span-2" data-testid="invite-org" />
        <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
          <SelectTrigger className="sm:col-span-3" data-testid="invite-plan"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="support">Ongoing Support</SelectItem><SelectItem value="enterprise">Enterprise Multi-site</SelectItem></SelectContent>
        </Select>
        <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
          <SelectTrigger className="sm:col-span-3" data-testid="invite-duration"><SelectValue /></SelectTrigger>
          <SelectContent>{DURATIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <SpecialityAccessFields idPrefix="invite" value={form} onChange={(v) => setForm({ ...form, ...v })} />
        <Button type="submit" disabled={busy} className="sm:col-span-6" data-testid="invite-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" /> Create account & send invite</>}</Button>
      </form>
      {result && <InviteResult r={result} onDismiss={() => setResult(null)} />}
    </div>
  );
}
